import React, { createContext, useContext, useState, ReactNode } from 'react';

type PopupType = 'error' | 'success';

interface PopupContextType {
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  hidePopup: () => void;
  popup: {
    show: boolean;
    message: string;
    type: PopupType;
  };
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const PopupProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [popup, setPopup] = useState<{
    show: boolean;
    message: string;
    type: PopupType;
  }>({
    show: false,
    message: '',
    type: 'success'
  });

  const showError = (message: string) => {
    setPopup({
      show: true,
      message,
      type: 'error'
    });
  };

  const showSuccess = (message: string) => {
    setPopup({
      show: true,
      message,
      type: 'success'
    });
  };

  const hidePopup = () => {
    setPopup(prev => ({
      ...prev,
      show: false
    }));
  };

  return (
    <PopupContext.Provider value={{ showError, showSuccess, hidePopup, popup }}>
      {children}
      {popup.show && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full mx-4 overflow-hidden transform transition-all animate-scaleIn">
            <div className={`p-4 flex items-center ${
              popup.type === 'error' 
                ? 'bg-red-600' 
                : 'bg-emerald-600'
            } text-white`}>
              {popup.type === 'error' ? (
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <h3 className="font-bold text-white">{popup.type === 'error' ? 'Error' : 'Success'}</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-center mb-4">{popup.message}</p>
              <div className="flex justify-center">
                <button
                  onClick={hidePopup}
                  className={`py-2 px-4 rounded transition-colors ${
                    popup.type === 'error'
                      ? 'bg-red-100 hover:bg-red-200 text-red-800'
                      : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800'
                  } font-medium`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (context === undefined) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
}; 